import React, { useMemo, useState } from 'react';
import { Helmet } from "react-helmet-async";
import {
    ConfigProvider,
    theme,
    Table,
    Input,
    Avatar,
    Tag,
    Button,
    Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    Minus, Plus, CircleChevronDown, Funnel,
    Phone, Clock, MessageSquareDashed, Paperclip, Smile, Send,
    CircleArrowUp, Undo2, RefreshCw, UserRoundPlus,
    CirclePercent, Bot, Check, CircleHelp, Waypoints
} from 'lucide-react';

import Layout from '../../Layout'; // Giữ nguyên import layout của bạn
import { Link } from 'react-router-dom';
import { LeftOutlined } from "@ant-design/icons";
import { mixformatCurrency } from 'services/mixin';

const { TextArea } = Input;

interface CommentItem {
    id: number,
    author: string,
    text: any,
    time: string,
}

interface ReportItem {
    id: number,
    name: string,
    unit: string,
    price: number,
    percent: number,
    money: number
}

// ================= DỮ LIỆU MOCK =================

export default function DebtContract() {
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [comments, setComments] = useState<CommentItem[]>([]);
    const [inputValue, setInputValue] = useState('');
    const handleSendComment = () => {
        if (!inputValue.trim()) return;
        const newComment = { id: Date.now(), author: 'Mr. Huấn', text: inputValue, time: 'Vừa xong' };
        setComments([...comments, newComment]);
        setInputValue('');
    };


    // ====Collapse States====
    const [expandedBlocks, setExpandedBlocks] = useState({
        general: true,
        contract: true,
        payTerm: true,
        phuluc: true,
        doanhso: true,
        relate: true,
        phutrach: true,
        history: true,
        comment: true,
    });

    const toggleBlock = (key: keyof typeof expandedBlocks) => {
        setExpandedBlocks(prev => ({ ...prev, [key]: !prev[key] }));
    };



    // ====SelectRow cua Seri List====
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
    };

    // ====Định nghĩa cột====
    const columns: ColumnsType<ReportItem> = [
        {
            title: "Sản phẩm / dịch vụ",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Đơn vị",
            dataIndex: "unit",
            key: "unit",
            align: "center",
        },
        {
            title: "Giá",
            dataIndex: "price",
            key: "price",
            align: "center",
            render: (text: any) => (
                <span>{mixformatCurrency(text, true)}</span>
            ),
        },
        {
            title: "% CK",
            dataIndex: "percent",
            key: "percent",
            align: "center",
            render: (text: any) => (
                <span>{text} %</span>
            ),
        },
        {
            title: "Tiền CK",
            dataIndex: "money",
            key: "money",
            align: "center",
            render: (text: any) => (
                <span>{mixformatCurrency(text, false)}</span>
            ),
        },

    ];

    const columns1 = [
        {
            title: "Từ ngày",
            dataIndex: "fromDate",
            key: "fromDate",
            align: "center" as const,
            render: (text: string, record: any) => ({
                children: text,
                props: {
                    rowSpan: record.rowSpan,
                },
            }),
        },
        {
            title: "Nhân viên",
            dataIndex: "employeeName",
            key: "employeeName",
            render: (text: string) => (
                <span className="text-gray-800 dark:text-gray-200">
                    {text}
                </span>
            ),
        },
        {
            title: "Phòng ban",
            dataIndex: "partment",
            key: "partment",
        },
        {
            title: "Vị trí",
            dataIndex: "position",
            key: "position",
        },
        {
            title: "Chức vụ",
            dataIndex: "role",
            key: "role",
        },
        {
            title: "% Hoa hồng",
            dataIndex: "commissionPercent",
            key: "commissionPercent",
            align: "center" as const,
            render: (text: number) => `${text}%`,
        },
        {
            title: "Hoa hồng",
            dataIndex: "commissionAmount",
            key: "commissionAmount",
            align: "right" as const,
            render: (text: number) => (
                <span>{mixformatCurrency(text, false)}</span>
            ),
        },
        {
            title: (
                <div className="flex items-center gap-1.5">
                    <span>Hoa hồng thực nhận</span>
                    <Tooltip title="Khi hợp đồng được thanh toán tổi thiểu 100% giá trị thì người phụ trách mới được hưởng tiền hoa hồng">
                        <CircleHelp size={12} className="text-orange-400 cursor-pointer" />
                    </Tooltip>
                </div>
            ),
            dataIndex: "actualCommission",
            key: "actualCommission",
            align: "right" as const,
            render: (text: number) => (
                <span>{mixformatCurrency(text, false)}</span>
            ),
        },
        {
            title: "% Doanh số",
            dataIndex: "doanhsoPercent",
            key: "doanhsoPercent",
            align: "center" as const,
            render: (text: number) => `${text}%`,
        },
        {
            title: "Doanh số",
            dataIndex: "doanhso",
            key: "doanhso",
            align: "right" as const,
            render: (text: number) => (
                <span>{mixformatCurrency(text, false)}</span>
            ),
        },
        {
            title: "Doanh số trước thuế",
            dataIndex: "doanhsotruocthue",
            key: "doanhsotruocthue",
            align: "right" as const,
            render: (text: number) => (
                <span>{mixformatCurrency(text, false)}</span>
            ),
        },
        {
            title: "Doanh số sau thuế",
            dataIndex: "doanhsosauthue",
            key: "doanhsosauthue",
            align: "right" as const,
            render: (text: number) => (
                <span>{mixformatCurrency(text, false)}</span>
            ),
        },
    ];

    const columns2 = [
        {
            title: "Từ ngày",
            dataIndex: "fromDate",
            key: "fromDate",
            align: "center" as const,
        },
        {
            title: "Nhân viên",
            dataIndex: "employeeName",
            key: "employeeName",
            render: (text: string) => (
                <span className="text-gray-800 dark:text-gray-200">
                    {text}
                </span>
            ),
        },
        {
            title: "Phòng ban",
            dataIndex: "partment",
            key: "partment",
        },
        {
            title: "Vị trí",
            dataIndex: "position",
            key: "position",
        },
        {
            title: "Chức vụ",
            dataIndex: "role",
            key: "role",
        },
    ];

    // ================= MOCK DATA BẢNG =================
    const reportData = [
        {
            id: 1,
            name: 'SP.062026.615 - Gói thiết kế thi công NTL',
            unit: 'Toàn bộ',
            price: 100000000,
            percent: 3,
            money: 0
        },
    ];

    const rawData = [
        {
            id: 1,
            fromDate: '18/08/2026',
            employeeName: 'Nguyễn Quang Huy RECO',
            partment: '',
            position: '',
            role: '',
            commissionPercent: 0,
            commissionAmount: 0,
            actualCommission: 0,
            doanhsoPercent: 0,
            doanhso: 100100000,
            doanhsotruocthue: 0,
            doanhsosauthue: 0,
        },
        {
            id: 2,
            fromDate: '18/08/2026',
            employeeName: 'Trần Nhật Minh RECO',
            partment: '',
            position: '',
            role: '',
            commissionPercent: 0,
            commissionAmount: 0,
            actualCommission: 0,
            doanhsoPercent: 100,
            doanhso: 0,
            doanhsotruocthue: 0,
            doanhsosauthue: 0,
        },
        {
            id: 3,
            fromDate: '19/08/2026',
            employeeName: 'Trần Nhật Minh RECO',
            partment: '',
            position: '',
            role: '',
            commissionPercent: 0,
            commissionAmount: 0,
            actualCommission: 0,
            doanhsoPercent: 100,
            doanhso: 0,
            doanhsotruocthue: 0,
            doanhsosauthue: 0,
        },
    ];

    const rawData1 = [
        {
            id: 1,
            fromDate: '18/08/2026',
            employeeName: 'Nguyễn Quang Huy RECO',
            partment: '',
            position: '',
            role: '',
        },
        {
            id: 2,
            fromDate: '18/08/2026',
            employeeName: 'Trần Nhật Minh RECO',
            partment: '',
            position: '',
            role: '',
        },
        {
            id: 3,
            fromDate: '19/08/2026',
            employeeName: 'Trần Nhật Minh RECO',
            partment: '',
            position: '',
            role: '',
        },
    ];

    //==== 2. HÀM TỰ ĐỘNG TÍNH ROWSPAN THEO TRƯỜNG KHÓA (fromDate)====
    const processRowSpan = <T extends Record<string, any>>(data: T[], key: keyof T) => {
        const result = [...data];
        let i = 0;

        while (i < result.length) {
            let count = 1;
            // Đếm các dòng liên tiếp có giá trị trùng nhau
            while (i + count < result.length && result[i][key] === result[i + count][key]) {
                count++;
            }

            // Gán rowSpan cho dòng đầu tiên của nhóm
            result[i] = { ...result[i], rowSpan: count };

            // Gán rowSpan = 0 cho các dòng tiếp theo trong cùng nhóm
            for (let j = 1; j < count; j++) {
                result[i + j] = { ...result[i + j], rowSpan: 0 };
            }

            i += count; // Nhảy tới nhóm tiếp theo
        }

        return result;
    };
    const tableData = useMemo(() => processRowSpan(rawData, 'fromDate'), []);


    // ================= THEME & UI COMPONENTS =================
    const antdThemeConfig = {
        token: {
            colorPrimary: '#3e91ab',
            colorBgContainer: isDarkMode ? '#1f2937' : '#ffffff',
            colorText: isDarkMode ? '#f3f4f6' : '#374151',
            colorBorder: isDarkMode ? '#4b5563' : '#d1d5db',
            borderRadius: 6,
        },
        components: { Table: { headerBg: isDarkMode ? '#374151' : '#f9fafb' } },
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
    };

    const CardHeader = ({ title, blockKey, extra }: { title: any, blockKey: keyof typeof expandedBlocks, extra?: any }) => (
        <div className="flex justify-between items-center mb-4 pb-2 border-b dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 px-4 py-2 -mx-4 -mt-4 rounded-t-lg">
            <h3 className="font-semibold text-gray-800 dark:text-gray-100">{title}</h3>
            <div className="flex items-center gap-3">
                {extra}
                <div onClick={() => toggleBlock(blockKey)} className="cursor-pointer text-gray-400 hover:text-[#3e91ab] p-1">
                    {expandedBlocks[blockKey] ? <Minus size={16} /> : <Plus size={16} />}
                </div>
            </div>
        </div>
    );

    return (
        <Layout>
            <Helmet>
                <title> Chi tiết hợp đồng | Khothi</title>
            </Helmet>
            <div className="flex gap-2 mb-4 justify-between items-center">
                <h2 className="text-lg text-gray-900 font-semibold dark:text-white">
                    Chi tiết hợp đồng
                </h2>
                {/* <button className="flex items-center gap-2 px-3 py-1.5 bg-[#3e91ab] text-white rounded text-sm font-medium hover:bg-[#357f96] transition-colors" onClick={() => navigate(`/employment-contract/add`)}>
                    <Plus size={16} /> Thêm mới
                </button> */}
            </div>
            <ConfigProvider theme={antdThemeConfig}>
                <div className={`min-h-screen pb-10 transition-colors duration-300`}>

                    {/* --- HEADER ACTIONS --- */}
                    <div className="border-b dark:border-gray-700 px-4 md:px-6 py-3 flex flex-wrap justify-between items-center gap-4 sticky top-[64px] bg-white dark:bg-[#1f1f1f] z-10 shadow-sm">
                        <div className="flex sm:items-center gap-4 text-sm sm:flex-row flex-col items-start">
                            <div className="flex sm:items-center gap-4 text-sm items-start">
                                <Link to="/debt" className="flex items-center gap-2">
                                    <LeftOutlined className="w-6 h-6 flex items-center justify-center rounded-full bg-[#3e91ab] hover:bg-[#357f96] text-white" />
                                </Link>
                                <span className="font-medium text-[#3e91ab] border-b-2 border-[#3e91ab] pb-1 cursor-pointer">Chi tiết</span>
                                <span className="text-gray-500 dark:text-gray-400 pb-1 font-medium">File HĐ/Hồ sơ thanh toán</span>
                                <span className="text-gray-500 dark:text-gray-400 pb-1 font-medium">Quy trình duyệt</span>
                            </div>
                        </div>

                        <div className='overflow-x-auto no-scrollbar'>
                            <div className="flex items-center gap-4 xl:gap-5 w-full xl:w-auto ml-auto min-w-max text-000000e0">
                                <Button type="primary" icon={<Bot size={18} />}>
                                    1AI Monitor
                                </Button>
                                <button className="text-sm flex items-center gap-1.5 hover:text-[#3e91ab]"><Undo2 size={14} /> Hoàn duyệt</button>
                                <button className="text-sm flex items-center gap-1.5 hover:text-[#3e91ab]"><RefreshCw size={14} /> Tình trạng</button>
                                <button className="flex items-center gap-1.5 hover:text-[#3e91ab] text-sm"><CirclePercent size={14} /> Doanh số</button>
                                <button className="flex items-center gap-1.5 hover:text-[#3e91ab] text-sm"><UserRoundPlus size={14} /> Phụ trách</button>
                                <button className="flex items-center gap-1.5 hover:text-[#3e91ab] text-sm"><CircleChevronDown size={20} /></button>
                            </div>
                        </div>
                    </div>

                    {/* --- MAIN LAYOUT --- */}
                    <div className="max-w-[1400px] mx-auto mt-4 grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">

                        {/* CỘT TRÁI CÓ TABS CHÍNH */}
                        <div className="min-w-0 overflow-hidden">
                            <div className="flex flex-col gap-6 pt-0">

                                {/* 1 */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                    <CardHeader title="Thông tin chung" blockKey="general" />
                                    {expandedBlocks.general && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8 text-[14px] mt-4">

                                            {/* Cột 1 */}
                                            <div className="flex flex-col gap-5">
                                                <div><p className="text-gray-400 mb-1">Mã hợp đồng</p><p className="font-medium text-gray-800 dark:text-gray-200">082026/HĐMB/30</p></div>
                                                <div><p className="text-gray-400 mb-1">Tên hợp đồng</p><p className="text-gray-800 dark:text-gray-200">Hợp đồng dịch vụ xây dựng (Hợp đồng nguyên tắc)</p></div>

                                                <div>
                                                    <p className="text-gray-400 mb-1">Người ký</p>
                                                    <div style={{ paddingLeft: '4px' }} className="inline-flex items-center  gap-1.5 px-2 py-0.5 rounded-full bg-[#f4f4f4] dark:bg-[#111827]">
                                                        <Avatar size={20} className="bg-gray-500 text-[10px]" src='https://trial.1office.vn/f/p/TDI0OHljd045SHpkUkF0d2REV0NRQ1pCZlJnU1dQUGZoTm5rT0dRMWVpYmFiREZsL2lDd1dNTFJwbEtUbnJjdVZVWkZ6T2FWU2ZSNkszZVpyeWxxWENWU29kQnBnNEgzWGM5NjVEL0xGNHVUSUNOWDRsaz0/1535570912638031ae4eb2b025045322.25.11.2022.small.png' />
                                                        <span className="text-000000e0">Lê Việt Thắng</span>
                                                    </div>
                                                </div>
                                                <div><p className="text-gray-400 mb-1">Loại hợp đồng</p><p className="text-gray-800 dark:text-gray-200">Hợp đồng nguyên tắc</p></div>
                                                <div><p className="text-gray-400 mb-1">Thành tiền</p><p className="text-gray-800 dark:text-gray-200">{mixformatCurrency(0, true)}</p></div>
                                                <div><p className="text-gray-400 mb-1">Tình trạng hợp đồng</p><p className="text-red-500">Nhận đủ hàng ( 18/08/2026 00:00 )</p></div>
                                                <div><p className="text-gray-400 mb-1">Đã chi</p><p className="text-gray-800 dark:text-gray-200">{mixformatCurrency(0, true)}</p></div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Trạng thái</p>
                                                    <Tag color="success">Đã duyệt</Tag>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Bước duyệt</p>
                                                    <Tag color="success">
                                                        Duyệt hợp đồng
                                                        <Check size={12} className="inline-block mr-0.5" />
                                                    </Tag>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Người theo dõi</p>
                                                    <div className='flex gap-[5px]' style={{ flexWrap: 'wrap' }}>
                                                        <div style={{ paddingLeft: '4px' }} className="inline-flex items-center mb-2 gap-1.5 px-2 py-0.5 rounded-full bg-[#f4f4f4] dark:bg-[#111827]">
                                                            <Avatar size={20} className="bg-gray-500 text-[10px]">R</Avatar>
                                                            <span className="text-000000e0">Nguyễn Quang Huy RECO</span>
                                                        </div>
                                                        <div style={{ paddingLeft: '4px' }} className="inline-flex items-center mb-2 gap-1.5 px-2 py-0.5 rounded-full bg-[#f4f4f4] dark:bg-[#111827]">
                                                            <Avatar size={20} className="bg-gray-500 text-[10px]">R</Avatar>
                                                            <span className="text-000000e0">Trần Nhật Minh RECO</span>
                                                        </div>
                                                        <div>
                                                            <Avatar size={24} className="border border-[#3e91ab] bg-transparent text-[#3e91ab] text-md   font-medium">+35</Avatar>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div><p className="text-gray-400 mb-1">Báo giá liên quan</p><p className="text-orange-600 dark:text-orange-400">0412201800005</p></div>

                                            </div>

                                            {/* Cột 2 */}
                                            <div className="flex flex-col gap-5">
                                                <div><p className="text-gray-400 mb-1">Khách hàng</p><p className="text-orange-600 dark:text-orange-400">KH_UY.082026.537 - Công Ty Cổ Phần Kinh Doanh Và Quản Lý Bất Động Sản Vinhomes</p></div>
                                                <div><p className="text-gray-400 mb-1">Kiểu hợp đồng</p><p className="text-gray-800 dark:text-gray-200">Hợp đồng mới</p></div>
                                                <div><p className="text-gray-400 mb-1">Ngày ký</p><p className="text-gray-800 dark:text-gray-200">18/08/2026</p></div>
                                                <div><p className="text-gray-400 mb-1">Hiệu lực hợp đồng</p><p className="text-gray-800 dark:text-gray-200">18/08/2026</p></div>
                                                <div><p className="text-gray-400 mb-1">Khuyến mại</p><p className="text-gray-800 dark:text-gray-200">--</p></div>
                                                <div><p className="text-gray-400 mb-1">Đã thu</p><p className="text-gray-800 dark:text-gray-200">{mixformatCurrency(0, true)}</p></div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Trạng thái</p>
                                                    <Tag color="success">Đang hiệu lực</Tag>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Người duyệt</p>
                                                    <div style={{ paddingLeft: '4px' }} className="inline-flex items-center  gap-1.5 px-2 py-0.5 rounded-full bg-[#f4f4f4] dark:bg-[#111827]">
                                                        <Avatar size={20} className="bg-gray-500 text-[10px]">R</Avatar>
                                                        <span className="text-000000e0">Nguyễn Quang Huy RECO</span>
                                                    </div>
                                                </div>
                                                <div><p className="text-gray-400 mb-1">Người phụ trách</p>
                                                    <p className="text-gray-800 dark:text-gray-200">
                                                        <span className='text-orange-600 dark:text-orange-400'>Nguyễn Quang Huy RECO</span>,
                                                        <span className='text-orange-600 dark:text-orange-400'>Trần Nhật Minh RECO</span>,
                                                        <span className='text-orange-600 dark:text-orange-400'>Nhân viên RECO</span>,

                                                    </p>
                                                </div>

                                                <div>
                                                    <p className="text-gray-400 mb-1">Trạng thái xuất kho</p>
                                                    <Tag color="error">Chưa xuất</Tag>
                                                </div>
                                                <div><p className="text-gray-400 mb-1">Mô tả</p><p className="text-gray-800 dark:text-gray-200">--</p></div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* 2 */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                    <CardHeader title="Chi tiết hợp đồng" blockKey="contract" />
                                    {expandedBlocks.contract && (
                                        <div className="animate-in fade-in duration-300">
                                            {/* Ant Design Table */}
                                            <ConfigProvider
                                                theme={{
                                                    components: {
                                                        Table: {
                                                            headerBg: "#f8f9fa",
                                                            headerColor: "#6b7280",
                                                            cellPaddingBlock: 12,
                                                            rowHoverBg: "#eff6ff",
                                                        },
                                                    },
                                                }}
                                            >
                                                <Table
                                                    rowSelection={rowSelection}
                                                    columns={columns}
                                                    dataSource={reportData}
                                                    pagination={false}
                                                    scroll={{ x: "max-content" }}
                                                />
                                            </ConfigProvider>
                                        </div>
                                    )}
                                </div>

                                {/* COT 3 */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                    <CardHeader title="Điều khoản thanh toán" blockKey="payTerm" />
                                    {expandedBlocks.payTerm && (
                                        <h1></h1>
                                    )}
                                </div>

                                {/* COT 4 */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                    <CardHeader title="Phụ lục hợp đồng" blockKey="phuluc" />
                                    {expandedBlocks.phuluc && (
                                        <h1></h1>
                                    )}
                                </div>

                                {/* COT 5 */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                    <CardHeader title="Doanh số, hoa hồng" blockKey="doanhso" />
                                    {expandedBlocks.doanhso && (
                                        <div className="animate-in fade-in duration-300">
                                            {/* Ant Design Table */}
                                            <ConfigProvider
                                                theme={{
                                                    components: {
                                                        Table: {
                                                            headerBg: "#f8f9fa",
                                                            headerColor: "#6b7280",
                                                            cellPaddingBlock: 12,
                                                            rowHoverBg: "#eff6ff",
                                                        },
                                                    },
                                                }}
                                            >
                                                <Table
                                                    columns={columns1}
                                                    dataSource={tableData}
                                                    pagination={false}
                                                    scroll={{ x: "max-content" }}
                                                    rowKey="id"
                                                />
                                            </ConfigProvider>
                                        </div>
                                    )}
                                </div>

                                {/*  COT 6*/}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                    <CardHeader title="Người phụ trách" blockKey="phutrach" />
                                    {expandedBlocks.phutrach && (
                                        <div className="animate-in fade-in duration-300">
                                            {/* Ant Design Table */}
                                            <ConfigProvider
                                                theme={{
                                                    components: {
                                                        Table: {
                                                            headerBg: "#f8f9fa",
                                                            headerColor: "#6b7280",
                                                            cellPaddingBlock: 12,
                                                            rowHoverBg: "#eff6ff",
                                                        },
                                                    },
                                                }}
                                            >
                                                <Table
                                                    columns={columns2}
                                                    dataSource={rawData1}
                                                    pagination={false}
                                                    scroll={{ x: "max-content" }}
                                                />
                                            </ConfigProvider>
                                        </div>
                                    )}
                                </div>

                                {/* COT 7 */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                    <CardHeader
                                        title="Liên quan"
                                        blockKey="relate"
                                        extra={<Waypoints size={16} className="cursor-pointer text-gray-400 hover:text-[#3e91ab]" />}
                                    />
                                    {expandedBlocks.relate && (
                                        <div className="animate-in fade-in duration-300 gap-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                            <div className="shadow-[0_1px_10px_0_rgba(0,0,0,0.16)] hover:shadow-none dark:shadow-none border border-transparent hover:border-slate-200 dark:border-slate-700/60 rounded-sm p-4 bg-white dark:bg-slate-800/40 transition-all duration-200">
                                                {/* Header: Avatar + Title + Subtitle */}
                                                <div className="flex items-start gap-3 mb-4">
                                                    <div className="w-8 h-8 rounded-full bg-[#0087BF] flex-shrink-0 flex items-center justify-center text-white font-medium text-sm">
                                                        R
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <h4 className="text-[14px] font-medium text-slate-800 dark:text-slate-100 leading-snug truncate">
                                                            [RECO] Dự án thiết kế kết cấu toà nhà thương mại dịch vụ ABC
                                                        </h4>
                                                        <div className="text-[11px] text-slate-400 dark:text-slate-400 flex items-center gap-1.5 mt-0.5 flex-wrap">
                                                            <span>Nguyễn Quang Huy RECO</span>
                                                            <Clock size={11} className="inline-block" />
                                                            <span>15:18 ng 18/8/2026</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Body Details: Responsive Grid */}
                                                <div className="grid grid-cols-[110px_1fr] sm:grid-cols-[130px_1fr] gap-y-2.5 text-[13px] pl-11">
                                                    {/* Row 1 */}
                                                    <div className="text-slate-500 dark:text-slate-400">Đối tượng</div>
                                                    <div className="text-slate-800 dark:text-slate-200 font-normal">Dự án</div>

                                                    {/* Row 2 */}
                                                    <div className="text-slate-500 dark:text-slate-400">Người thực hiện</div>
                                                    <div className="text-slate-400 dark:text-slate-500">--</div>

                                                    {/* Row 3 */}
                                                    <div className="text-slate-500 dark:text-slate-400 flex items-center">Trạng thái</div>
                                                    <div>
                                                        <span className="inline-block px-2.5 py-0.5 text-[12px] text-[#2F80ED] bg-[#E8F1FD] dark:bg-blue-900/40 dark:text-blue-300 rounded-md font-normal">
                                                            Đang thực hiện
                                                        </span>
                                                    </div>

                                                    {/* Row 4 */}
                                                    <div className="text-slate-500 dark:text-slate-400">Kết thúc thực tế</div>
                                                    <div className="text-slate-400 dark:text-slate-500">--</div>
                                                </div>
                                            </div>

                                            <div className="shadow-[0_1px_10px_0_rgba(0,0,0,0.16)] hover:shadow-none dark:shadow-none border border-transparent hover:border-slate-200 dark:border-slate-700/60 rounded-sm p-4 bg-white dark:bg-slate-800/40 transition-all duration-200">
                                                {/* Header: Avatar + Title + Subtitle */}
                                                <div className="flex items-start gap-3 mb-4">
                                                    <div className="w-8 h-8 rounded-full bg-[#0087BF] flex-shrink-0 flex items-center justify-center text-white font-medium text-sm">
                                                        R
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <h4 className="text-[14px] font-medium text-slate-800 dark:text-slate-100 leading-snug truncate">
                                                            Công Ty Cổ Phần Kinh Doanh Và Quản Lý Bất Động Sản Vinhomes
                                                        </h4>
                                                        <div className="text-[11px] text-slate-400 dark:text-slate-400 flex items-center gap-1.5 mt-0.5 flex-wrap">
                                                            <span>Nguyễn Quang Huy RECO</span>
                                                            <Clock size={11} className="inline-block" />
                                                            <span>15:18 ng 18/8/2026</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Body Details: Responsive Grid */}
                                                <div className="grid grid-cols-[110px_1fr] sm:grid-cols-[130px_1fr] gap-y-2.5 text-[13px] pl-11">
                                                    {/* Row 1 */}
                                                    <div className="text-slate-500 dark:text-slate-400">Đối tượng</div>
                                                    <div className="text-slate-800 dark:text-slate-200 font-normal">Khách hàng</div>

                                                    {/* Row 2 */}
                                                    <div className="text-slate-500 dark:text-slate-400">Điện thoại</div>
                                                    <div>
                                                        <div style={{ paddingLeft: '4px' }} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/30 text-[11px] font-medium">
                                                            <Avatar size={20} className="bg-[#f96800] text-[10px]"><Phone size={15}></Phone></Avatar>
                                                            <span className="text-orange-600 dark:text-orange-400">0906336162</span>
                                                        </div>
                                                    </div>

                                                    <div className="text-slate-500 dark:text-slate-400">Nguồn khách hàng</div>
                                                    <div className="text-slate-800 dark:text-slate-200 font-normal">Facebook</div>

                                                </div>
                                            </div>

                                            <div className="shadow-[0_1px_10px_0_rgba(0,0,0,0.16)] hover:shadow-none dark:shadow-none border border-transparent hover:border-slate-200 dark:border-slate-700/60 rounded-sm p-4 bg-white dark:bg-slate-800/40 transition-all duration-200">
                                                {/* Header: Avatar + Title + Subtitle */}
                                                <div className="flex items-start gap-3 mb-4">
                                                    <div className="w-8 h-8 rounded-full bg-[#0087BF] flex-shrink-0 flex items-center justify-center text-white font-medium text-sm">
                                                        R
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <h4 className="text-[14px] font-medium text-slate-800 dark:text-slate-100 leading-snug truncate">
                                                            abc
                                                        </h4>
                                                        <div className="text-[11px] text-slate-400 dark:text-slate-400 flex items-center gap-1.5 mt-0.5 flex-wrap">
                                                            <span>Admin</span>
                                                            <Clock size={11} className="inline-block" />
                                                            <span>15:18 ng 18/8/2026</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Body Details: Responsive Grid */}
                                                <div className="grid grid-cols-[110px_1fr] sm:grid-cols-[130px_1fr] gap-y-2.5 text-[13px] pl-11">
                                                    {/* Row 1 */}
                                                    <div className="text-slate-500 dark:text-slate-400">Đối tượng</div>
                                                    <div className="text-slate-800 dark:text-slate-200 font-normal">Báo giá</div>

                                                    {/* Row 2 */}
                                                    <div className="text-slate-500 dark:text-slate-400">Khách hàng</div>
                                                    <div className="text-slate-800 dark:text-slate-200 font-normal">Đình Tuấn</div>

                                                    <div className="text-slate-500 dark:text-slate-400">Giá trị báo giá</div>
                                                    <div className="text-slate-800 dark:text-slate-200 font-normal">{mixformatCurrency(62000000, true)}</div>

                                                    <div className="text-slate-500 dark:text-slate-400">Trạng thái</div>
                                                    <div>
                                                        <Tag color="success">Đã duyệt</Tag>
                                                    </div>

                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>


                            </div>
                        </div>

                        {/* CỘT PHẢI (THẢO LUẬN / LỊCH SỬ) - Giữ nguyên logic cũ */}
                        <div className="min-w-0 flex flex-col gap-4 h-full">

                            <div className="min-w-0 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                <CardHeader
                                    title="Lịch sử hoạt động"
                                    blockKey="history"
                                    extra={<Funnel size={16} className="cursor-pointer text-gray-400 hover:text-[#3e91ab]" />}
                                />
                                {expandedBlocks.history && (
                                    <div className="pb-4 mt-4">
                                        {/* Khung chứa Timeline */}
                                        <div className="relative pl-6 space-y-4 max-h-[450px] overflow-y-auto pr-1">

                                            {/* Trục dọc (Đường line màu xám chạy dọc timeline) */}
                                            <div className="absolute left-[11px] top-3 bottom-3 w-0.5 bg-gray-200 dark:bg-gray-700"></div>

                                            {/* --- ITEM TIMELINE 1 --- */}
                                            <div className="relative flex items-start group">
                                                {/* Icon tròn màu hồng đậm bên trái */}
                                                <div className="absolute -left-6 top-2 w-6 h-6 rounded-full bg-[#00BBA5] text-white flex items-center justify-center shadow-sm z-9">
                                                    <CirclePercent size={12} />
                                                </div>
                                                {/* Card nội dung */}
                                                <div className="flex-1 bg-[#f8f9fa] dark:bg-gray-800 p-4 rounded-md border border-gray-100 dark:border-gray-700 relative ml-2">
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <p className="text-[13px] text-gray-800 dark:text-gray-200">
                                                                <span className="font-bold">Nguyễn Quang Huy RECO</span> <span className="text-gray-500"> đã cập nhật doanh số</span>
                                                            </p>
                                                            <p className="text-[11px] text-gray-400 mt-0.5 flex items-center">
                                                                <span className="mr-1"></span> 17:26 ng 25/06/2026
                                                            </p>
                                                            <p className="text-[12px] text-gray-600 dark:text-gray-400 mt-2">
                                                                +  Nguyễn Quang Huy RECO   - Thời gian: 18/08/2026 +  Trần Nhật Minh RECO   - Thời gian: 18/08/2026   - Doanh số: 100%   - Hoa hồng :  +  Nhân viên RECO   - Thời gian: 18/08/2026   - Doanh số: 100%   - Hoa hồng :
                                                            </p>
                                                        </div>
                                                        {/* Avatar chữ B bên phải */}
                                                        <div className="w-8 h-8 rounded-full bg-gray-400 text-white flex items-center justify-center text-xs font-bold shrink-0 ml-3">
                                                            B
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="relative flex items-start group">
                                                {/* Icon tròn màu hồng đậm bên trái */}

                                                <div className="absolute -left-6 top-2 w-6 h-6 rounded-full bg-[#2F7DFF] text-white flex items-center justify-center shadow-sm z-9">
                                                    <RefreshCw size={12} />
                                                </div>
                                                {/* Card nội dung */}
                                                <div className="flex-1 bg-[#f8f9fa] dark:bg-gray-800 p-4 rounded-md border border-gray-100 dark:border-gray-700 relative ml-2">
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <p className="text-[13px] text-gray-800 dark:text-gray-200">
                                                                <span className="font-bold">Nguyễn Quang Huy RECO</span> <span className="text-gray-500"> đã cập nhật tình trạng</span>
                                                            </p>
                                                            <p className="text-[11px] text-gray-400 mt-0.5 flex items-center">
                                                                <span className="mr-1"></span> 17:26 ng 25/06/2026
                                                            </p>
                                                            <p className="text-[12px] text-gray-600 dark:text-gray-400 mt-2">
                                                                Cập nhật tình trạng sang <b> Nhận đủ hàng</b></p>
                                                        </div>
                                                        {/* Avatar chữ B bên phải */}
                                                        <div className="w-8 h-8 rounded-full bg-gray-400 text-white flex items-center justify-center text-xs font-bold shrink-0 ml-3">
                                                            B
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="relative flex items-start group">
                                                {/* Icon tròn màu hồng đậm bên trái */}
                                                <div className="absolute -left-6 top-2 w-6 h-6 rounded-full bg-[#13CE74] text-white flex items-center justify-center shadow-sm z-9">
                                                    <Check size={12} />
                                                </div>
                                                {/* Card nội dung */}
                                                <div className="flex-1 bg-[#f8f9fa] dark:bg-gray-800 p-4 rounded-md border border-gray-100 dark:border-gray-700 relative ml-2">
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <p className="text-[13px] text-gray-800 dark:text-gray-200">
                                                                <span className="font-bold">Nguyễn Quang Huy RECO</span> <span className="text-gray-500"> đã duyệt</span>
                                                            </p>
                                                            <p className="text-[11px] text-gray-400 mt-0.5 flex items-center">
                                                                <span className="mr-1"></span> 17:26 ng 25/06/2026
                                                            </p>
                                                            <p className="text-[12px] text-gray-600 dark:text-gray-400 mt-2">
                                                                Bước: Duyệt hợp đồng (#3)</p>
                                                        </div>
                                                        {/* Avatar chữ B bên phải */}
                                                        <div className="w-8 h-8 rounded-full bg-gray-400 text-white flex items-center justify-center text-xs font-bold shrink-0 ml-3">
                                                            B
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="relative flex items-start group">
                                                {/* Icon tròn màu hồng đậm bên trái */}
                                                <div className="absolute -left-6 top-2 w-6 h-6 rounded-full bg-[#13CE74] text-white flex items-center justify-center shadow-sm z-9">
                                                    <Plus size={12} />
                                                </div>
                                                {/* Card nội dung */}
                                                <div className="flex-1 bg-[#f8f9fa] dark:bg-gray-800 p-4 rounded-md border border-gray-100 dark:border-gray-700 relative ml-2">
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <p className="text-[13px] text-gray-800 dark:text-gray-200">
                                                                <span className="font-bold">Nguyễn Quang Huy RECO</span> <span className="text-gray-500"> đã tạo mới hợp đồng</span>
                                                            </p>
                                                            <p className="text-[11px] text-gray-400 mt-0.5 flex items-center">
                                                                <span className="mr-1"></span> 17:26 ng 25/06/2026
                                                            </p>
                                                            <p className="text-[12px] text-gray-600 dark:text-gray-400 mt-2">
                                                                ID: 1093 - 082026/HĐMB/30</p>
                                                        </div>
                                                        {/* Avatar chữ B bên phải */}
                                                        <div className="w-8 h-8 rounded-full bg-gray-400 text-white flex items-center justify-center text-xs font-bold shrink-0 ml-3">
                                                            B
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                <div className="flex justify-between items-center mb-4 pb-2 border-b dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 px-4 py-2 -mx-4 -mt-4 rounded-t-lg">
                                    <div className="flex sm:items-center gap-4 text-sm sm:flex-row flex-col items-start">
                                        <div className="flex sm:items-center gap-4 text-sm items-start">
                                            <span className="font-medium text-[#3e91ab] border-b-2 border-[#3e91ab] pb-1 cursor-pointer">Thảo luận</span>
                                        </div>
                                        <span className="text-gray-500 dark:text-gray-400 pb-1 font-medium">Đính kèm</span>
                                    </div>
                                    <div onClick={() => toggleBlock('comment')} className="cursor-pointer text-gray-400 hover:text-[#3e91ab] p-1">
                                        {expandedBlocks['comment'] ? <Minus size={16} /> : <Plus size={16} />}
                                    </div>
                                </div>
                                {expandedBlocks.comment && (
                                    <div className="flex flex-col mt-2 relative">

                                        {/* Khu vực hiển thị bình luận */}
                                        <div className="flex-1 overflow-y-auto mb-4 pr-2">
                                            {comments.length === 0 ? (
                                                <div className="flex flex-col items-center justify-center text-gray-400 opacity-60">
                                                    <MessageSquareDashed size={48} className="mb-3" strokeWidth={1} />
                                                    <p className="text-sm">Không có thảo luận nào</p>
                                                </div>
                                                // EMPTY STATE NẾU KHÔNG CÓ BÌNH LUẬN
                                            ) : (
                                                // NẾU CÓ BÌNH LUẬN
                                                comments.map(comment => (
                                                    <div key={comment.id} className="flex gap-3 mb-6">
                                                        <Avatar style={{ backgroundColor: '#84cc16' }}>{comment.author.charAt(0)}</Avatar>
                                                        <div className="flex-1">
                                                            <span className="font-semibold text-gray-800 dark:text-gray-200 text-sm">{comment.author}</span>
                                                            <div className="bg-gray-100 dark:bg-gray-700/50 p-3 rounded-lg rounded-tl-none mt-1 text-sm text-gray-800 w-fit">
                                                                {comment.text}
                                                            </div>
                                                            <div className="flex items-center gap-4 mt-2 text-[11px] text-gray-500">
                                                                <span>{comment.time}</span>
                                                                <span className="cursor-pointer hover:text-[#3e91ab]">Thích</span>
                                                                <span className="cursor-pointer hover:text-[#3e91ab]">Trả lời</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>

                                        {/* Ô Input nhập liệu */}
                                        <div className="flex justify-between border border-gray-200 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-700/30 shrink-0 mt-auto">
                                            <div className="flex items-center gap-2 mb-2">
                                                <CircleArrowUp className="cursor-pointer text-gray-400 hover:text-[#3e91ab]" size={16} />
                                                <Input
                                                    bordered={false}
                                                    placeholder="Viết thảo luận..."
                                                    className="flex-1 text-sm"
                                                    value={inputValue}
                                                    onChange={(e) => setInputValue(e.target.value)}
                                                    onPressEnter={handleSendComment}
                                                />
                                            </div>
                                            <div className="flex gap-3 items-center text-gray-400 pl-8 pr-2">
                                                <Paperclip size={16} className="cursor-pointer hover:text-[#3e91ab]" />
                                                <Smile size={16} className="cursor-pointer hover:text-[#3e91ab]" />
                                                <Send size={16} className="cursor-pointer text-[#3e91ab]" />
                                            </div>
                                        </div>

                                    </div>
                                )}

                            </div>
                        </div>
                    </div>

                </div>
            </ConfigProvider>
        </Layout>
    );
}